// Shared pieces for the CS Import pages (read-only commonsku archive).
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Download, FileQuestion, X } from "lucide-react";
import { CS_FILE_KIND, csCanPreview, csDay, csFileSize, type CsFile } from "@/lib/csArchive";

export function CsNotReady() {
  return (
    <div className="mx-auto mt-16 max-w-xl rounded-lg bg-canvas p-8 text-center">
      <h1 className="text-[24px] font-semibold text-ink">CS Import is almost ready</h1>
      <p className="mt-3 text-[15px] text-muted">The commonsku archive switches on once its database step has been run. Nothing else in Flow is affected.</p>
    </div>
  );
}

export function Crumbs({ children }: { children: ReactNode }) {
  return <p className="mt-2 text-[14px] text-muted"><Link to="/cs-import" className="hover:text-ink">CS Import</Link>{children}</p>;
}

export function SideCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <aside className="rounded-lg border border-rule bg-white p-6">
      <h3 className="text-[19px] font-semibold tracking-[-0.3px] text-ink">{title}</h3>
      <dl className="mt-4 space-y-3 text-[15px]">{children}</dl>
    </aside>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-semibold text-ink">{children || "—"}</dd>
    </div>
  );
}

/** Missing or failed files are said plainly — never a dead link. */
export function MissingNote({ children }: { children: ReactNode }) {
  return <span className="inline-flex items-center gap-1.5 rounded bg-amber-50 px-2 py-0.5 text-[13px] text-amber-800"><FileQuestion className="h-3.5 w-3.5" aria-hidden />{children}</span>;
}

export function FileLine({ f, detail }: { f: CsFile; detail?: ReactNode }) {
  const stored = f.state === "stored" && f.url;
  const isImage = (f.mime_type ?? "").startsWith("image/") && !/tiff/.test(f.mime_type ?? "");
  return (
    <li className="flex items-center gap-4 border-b border-rule py-4">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-canvas text-[12px] font-semibold text-muted">
        {stored && isImage ? <img src={f.url!} alt="" className="h-full w-full object-contain" loading="lazy" /> : (f.name.split(".").pop() ?? "FILE").slice(0, 4).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[16px] font-semibold text-ink">{f.name}</p>
        <p className="mt-0.5 text-[14px] text-muted">
          {[CS_FILE_KIND[f.kind] ?? f.kind, csFileSize(f.size_bytes), f.uploaded_on ? `added ${csDay(f.uploaded_on)}` : null, f.uploaded_by].filter(Boolean).join(" · ")}
        </p>
        {detail ? <div className="mt-1 text-[14px] text-muted">{detail}</div> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!stored ? <MissingNote>{f.state === "missing" ? "Not in commonsku" : f.state === "failed" ? "Couldn't be copied" : "Not copied yet"}</MissingNote> : null}
        {stored && csCanPreview(f.mime_type, f.name) ? <PreviewButton f={f} /> : null}
        {stored && f.download_url ? <a href={f.download_url} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule bg-white px-3 text-[14px] font-medium text-ink hover:bg-canvas"><Download className="h-4 w-4" aria-hidden />Download</a> : null}
      </div>
    </li>
  );
}

/** Opens a file in a preview window over the page (Flow keeps one tab). */
export function PreviewButton({ f, label = "Open" }: { f: CsFile; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex h-10 items-center rounded-md border border-rule bg-white px-3 text-[14px] font-medium text-ink hover:bg-canvas">{label}</button>
      {open ? <FilePreview f={f} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function FilePreview({ f, onClose }: { f: CsFile; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const isImage = (f.mime_type ?? "").startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(f.name);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-brown-500/50 p-4 md:p-10" role="dialog" aria-modal="true" aria-label={f.name} onClick={onClose}>
      <div className="flex h-full max-h-[94vh] w-full max-w-[1200px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between gap-3 border-b border-rule px-5 py-3">
          <p className="truncate text-[16px] font-semibold text-ink">{f.name}</p>
          <div className="flex shrink-0 gap-2">
            {f.download_url ? <a href={f.download_url} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-[14px] font-medium text-ink hover:bg-canvas"><Download className="h-4 w-4" aria-hidden />Download</a> : null}
            <button type="button" onClick={onClose} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-[14px] font-medium text-ink hover:bg-canvas"><X className="h-4 w-4" aria-hidden />Close</button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-auto bg-canvas">
          {isImage ? <img src={f.url!} alt={f.name} className="mx-auto max-h-full max-w-full object-contain p-6" /> : <iframe title={f.name} src={f.url!} className="h-full min-h-[70vh] w-full bg-white" />}
        </div>
      </div>
    </div>
  );
}
